import React, { createContext, useState, useContext, ReactNode, useRef } from 'react';

type CameraModalContextType = {
  isOpen: boolean;
  openCameraModal: (callback: (images: File[], mode?: 'idcard' | 'normal') => void) => void;
  closeCameraModal: () => void;
  handleCapture: (images: File[], mode?: 'idcard' | 'normal') => void;
};

const CameraModalContext = createContext<CameraModalContextType | undefined>(undefined);

export const CameraModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Use a ref instead of state to avoid closures capturing stale callback references
  const callbackRef = useRef<((images: File[], mode?: 'idcard' | 'normal') => void) | null>(null);

  // Combined function to set callback and open modal
  const openCameraModal = (callback: (images: File[], mode?: 'idcard' | 'normal') => void) => {
    if (!callback) {
      console.error("Camera capture callback is required");
      return;
    }
    
    console.log("Setting capture callback");
    callbackRef.current = callback;
    setIsOpen(true);
  };
  
  const closeCameraModal = () => {
    setIsOpen(false);
    // No need to reset callback immediately - it will be overridden on next open
  };

  // This function will be passed to the CameraCaptureModal component
  const handleCapture = (images: File[], mode?: 'idcard' | 'normal') => {
    console.log("Context handleCapture called with mode:", mode);
    
    if (callbackRef.current) {
      // Explicitly pass both parameters to ensure mode is included
      callbackRef.current(images, mode);
    }
    
    // Close the modal after handling the capture
    closeCameraModal();
  };

  
  return (
    <CameraModalContext.Provider
      value={{
        isOpen,
        openCameraModal,
        closeCameraModal,
        handleCapture
      }}
    >
      {children}
    </CameraModalContext.Provider>
  );
};

export const useCameraModal = (): CameraModalContextType => {
  const context = useContext(CameraModalContext);
  if (context === undefined) {
    throw new Error('useCameraModal must be used within a CameraModalProvider');
  }
  return context;
};
